/**
 * Global registry of components that currently have unsaved changes.
 *
 * Fed by `useUnsavedChangesWarning` so cross-cutting concerns (e.g. the silent
 * version auto-refresh in VersionChecker) can avoid reloading the page while the
 * user has unsaved form data — which would otherwise pop a surprising native
 * "Leave site?" prompt or risk losing their work.
 *
 * This is intentionally module-level state (not React state): the consumers are
 * non-rendering side effects that need a synchronous, render-independent answer.
 *
 * Coverage note: only forms that route their dirty state through
 * `useUnsavedChangesWarning` are tracked here. A form with its own ad-hoc dirty
 * tracking is invisible to `hasUnsavedChanges()` and relies solely on the
 * browser's native `beforeunload` prompt as a safety net.
 */
const dirtyTokens = new Set<symbol>();

/** Mark a token as having unsaved changes. */
export function registerUnsavedChanges(token: symbol): void {
  dirtyTokens.add(token);
}

/** Clear the unsaved-changes mark for a token. */
export function unregisterUnsavedChanges(token: symbol): void {
  dirtyTokens.delete(token);
}

/** True if any registered component currently has unsaved changes. */
export function hasUnsavedChanges(): boolean {
  return dirtyTokens.size > 0;
}
